import dynamic from 'next/dynamic';

import { DecoratorWrapper } from '@natu/ui/Decorators/DecoratorWrapper';

import {
  type BlokItem,
  DynamicRender,
  type SBProps,
  type Spacing,
  type StoryblokAsset,
  getAssetFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
} from '../../../../utils';

const Rating = dynamic(() => import('@natu/ui/Rating').then(mod => mod.Rating));

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

interface SBGuaranteeSectionProps {
  authorName?: string;
  authorDesc?: string;
  authorImage?: StoryblokAsset;
  ratingValue?: string;
  content?: BlokItem[];
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;
}

export const SBGuaranteeSection = ({ blok }: SBProps<SBGuaranteeSectionProps>) => {
  const {
    ratingValue,
    content,
    authorName,
    authorDesc,
    authorImage,
    mbMobile,
    mbTablet,
    mbDesktop,
    mtMobile,
    mtTablet,
    mtDesktop,
  } = blok;

  const image = getAssetFromStoryblok(authorImage, { type: 'image' });

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
    className: 'flex w-full flex-col items-center relative',
  });

  return (
    <DecoratorWrapper
      className={className}
      component="section"
      variant="default"
      {...sbEditable(blok)}
    >
      {ratingValue && <Rating count={5} value={ratingValue || '5.0'} className="mb-8 lg:mb-6" />}
      <DynamicRender
        data={content}
        parentProps={{ className: 'max-w-[350px] md:max-w-[1100px] md:px-20' }}
      />
      {image?.src && authorDesc && authorName && (
        <div className="flex flex-col items-center gap-4 lg:flex-row lg:gap-6">
          <ResponsiveImage className="w-16 lg:w-[72px]" {...image} />
          <div className="flex flex-col items-center justify-center gap-1 lg:items-start lg:gap-0">
            <span className="text-lg font-semibold text-black xl:text-xl">{authorName}</span>
            <span className="text-md text-gray-600">{authorDesc}</span>
          </div>
        </div>
      )}
    </DecoratorWrapper>
  );
};
